import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button, Small } from "@fronterahealth/frontera-ui-components";

import { ReportGoalType, useCreateUpdateReportGoalMutation } from "@api/graphql/types-and-hooks";
import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

/**
 * The `reportGoals` query nests goals inside their domain (`domain.reportgoalSet`)
 * and doesn't select `reportDomain` on each goal, so we have to thread the
 * parent domain id alongside each goal — without it, createUpdateReportGoal
 * receives `reportDomainId: null` and orphans every goal off its domain.
 */
export interface GoalWithDomain {
  goal: ReportGoalType;
  domainId: string;
}

type ApplyMode = "all" | "emptyOnly";

interface BaselineDateBannerProps {
  /** Controlled date value in yyyy-MM-dd form. Empty string = no date set. */
  value: string;
  onChange: (next: string) => void;
  /** Every in-use goal across all domains, each tagged with its parent domain id. */
  goals: GoalWithDomain[];
  /** Report (assessment) id — required by createUpdateReportGoal alongside the goal id. */
  reportId: string;
  learnerId: string;
  refetch: () => Promise<unknown>;
  className?: string;
}

export const BaselineDateBanner: React.FC<BaselineDateBannerProps> = ({
  value,
  onChange,
  goals,
  reportId,
  learnerId,
  refetch,
  className,
}) => {
  const queryClient = useQueryClient();
  const [isApplying, setIsApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Synchronous in-flight lock so rapid clicks can't slip past the React
  // re-render between disable-onClick and the isApplying state flip.
  const inFlightRef = useRef(false);

  // The input stays editable while the overwrite-confirmation dialog is open.
  // If the user clears it, close the dialog so a confirm click can't dead-end
  // at runApply's empty-date guard (which would silently no-op).
  useEffect(() => {
    if (!value) setConfirmOpen(false);
  }, [value]);

  const { mutateAsync: updateGoal } = useCreateUpdateReportGoalMutation({});

  const goalsWithExistingDate = goals.filter(({ goal }) => !!goal.baselineDate);
  const goalsWithoutDate = goals.filter(({ goal }) => !goal.baselineDate);
  const canApply = !!value && goals.length > 0 && !isApplying;

  // createUpdateReportGoal requires goalName + reportId + reportDomainId to be
  // present on every call. Without them the server strips the domain
  // association and the goal disappears from the UI.
  const buildGoalInput = ({ goal, domainId }: GoalWithDomain, baselineDate: string) => ({
    reportGoalId: goal.id,
    goalName: goal.goalName,
    reportId,
    reportDomainId: domainId,
    baselineDate,
  });

  // createUpdateReportGoal resolves with `{ status: false }` for business-level
  // errors instead of rejecting, so promise-level success isn't enough — we
  // also have to inspect the payload to count real successes.
  const countSuccesses = (
    results: PromiseSettledResult<Awaited<ReturnType<typeof updateGoal>>>[],
  ): { successes: number; failures: number } => {
    let successes = 0;
    for (const r of results) {
      if (r.status === "fulfilled" && r.value.createUpdateReportGoal?.status) successes++;
    }
    return { successes, failures: results.length - successes };
  };

  // The goal side-panel reads from its own ["getReportGoal", goalId] query,
  // which the list-level refetch doesn't touch. Invalidate it so any open
  // panel reflects the new baseline date. Errors here are swallowed — the
  // mutations have already persisted, so a refresh blip shouldn't block the
  // success toast.
  const refreshAfterMutation = async () => {
    try {
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ["getReportGoal"] });
    } catch (err) {
      console.error("Failed to refresh after baseline-date bulk mutation", err);
    }
  };

  const applyBaselineDate = async (target: string, mode: ApplyMode): Promise<number> => {
    const targets = mode === "emptyOnly" ? goalsWithoutDate : goals;
    if (targets.length === 0) return 0;
    const results = await Promise.allSettled(
      targets.map((entry) => updateGoal({ learnerId, goalInput: buildGoalInput(entry, target) })),
    );
    const { successes, failures } = countSuccesses(results);
    if (failures > 0) {
      notifyError(`Failed to update baseline date on ${failures} goal${failures === 1 ? "" : "s"}`);
    }
    await refreshAfterMutation();
    return successes;
  };

  const runApply = async (mode: ApplyMode) => {
    if (inFlightRef.current) return;
    // Confirmation dialog leaves the input editable, so re-validate before
    // committing — confirming with an empty value would otherwise bulk-clear
    // every goal's baseline date.
    if (!value) return;
    inFlightRef.current = true;

    setIsApplying(true);
    try {
      const appliedCount = await applyBaselineDate(value, mode);
      if (appliedCount > 0) {
        notifySuccess(`Applied baseline date to ${appliedCount} goal${appliedCount === 1 ? "" : "s"}`);
      }
    } finally {
      setIsApplying(false);
      inFlightRef.current = false;
    }
  };

  const handleApplyClick = () => {
    if (!canApply || inFlightRef.current) return;
    if (goalsWithExistingDate.length > 0) {
      setConfirmOpen(true);
    } else {
      void runApply("all");
    }
  };

  // "Apply only to empty" only makes sense when there are both populated and
  // empty goals — if every goal already has a date, the empty-only option
  // would no-op.
  const showApplyEmptyOnly = goalsWithExistingDate.length > 0 && goalsWithoutDate.length > 0;

  // Counts live in the body copy, not on the CTAs, so the buttons stay short
  // and consistent across configurations. Verb agreement: "1 of N has" vs
  // "M of N have"; "1 that is" vs "K that are".
  const dialogBody = (() => {
    const withCount = goalsWithExistingDate.length;
    const withoutCount = goalsWithoutDate.length;
    const total = goals.length;
    if (!showApplyEmptyOnly) {
      const lead =
        total === 1
          ? "This goal already has a baseline date set."
          : `All ${total} goals already have a baseline date set.`;
      return `${lead} Applying will overwrite ${total === 1 ? "it" : "them"}.`;
    }
    const hasVerb = withCount === 1 ? "has" : "have";
    const missingVerb = withoutCount === 1 ? "is" : "are";
    const remainingNoun = withoutCount === 1 ? "goal" : "goals";
    return `${withCount} of ${total} goals already ${hasVerb} a baseline date set. You can overwrite all of them, or only fill in the ${withoutCount} ${remainingNoun} that ${missingVerb} missing baseline date.`;
  })();

  return (
    <>
      <div
        className={twMerge(
          "flex items-center gap-3 rounded-md border border-border-strong bg-white px-4 py-2.5",
          className,
        )}
      >
        <Small displayType="loud" colorType="primary">
          Set baseline date for all goals
        </Small>
        <input
          type="date"
          value={value}
          max="9999-12-31"
          onChange={(e) => onChange(e.target.value)}
          className="rounded-md border border-limestone-300 bg-white px-2 py-1 text-sm text-text-primary focus:outline-hidden focus:ring-2 focus:ring-interactive-primary-default"
          aria-label="Baseline date for all goals"
        />
        <Button
          text={isApplying ? "Applying…" : "Apply"}
          appearance="link"
          disabled={!canApply}
          onClick={handleApplyClick}
        />
      </div>

      <ConfirmationDialog
        open={confirmOpen}
        title="Apply baseline date to all goals?"
        content={dialogBody}
        submitButtonText="Apply to all"
        submitButtonAction="regular"
        submitButtonLoading={isApplying}
        onSubmitClick={async () => {
          setConfirmOpen(false);
          await runApply("all");
        }}
        // When both populated and empty goals exist, the dialog reads
        // "Cancel | Apply only to empty | Apply to all" left-to-right —
        // secondary (Mosaic's border style) holds the alternative action and
        // tertiary holds Cancel. When only overwrite is possible the layout
        // collapses to the standard "Cancel | Apply to all".
        cancelButtonText={showApplyEmptyOnly ? "Apply only to empty" : "Cancel"}
        onCancelClick={async () => {
          setConfirmOpen(false);
          if (showApplyEmptyOnly) await runApply("emptyOnly");
        }}
        tertiaryButtonText={showApplyEmptyOnly ? "Cancel" : undefined}
        onTertiaryClick={showApplyEmptyOnly ? () => setConfirmOpen(false) : undefined}
      />
    </>
  );
};
